import { App } from '../BMapsApp';
import { EventBroker } from '../eventbroker';
import { ResponseIds } from '../server/server_common';
import { UserCmd, UserActions } from './UserCmd';
import { SimSpec } from '../model/SimSpec';
import { integrateFragments, doWithActiveSolute } from './cmds_common';
import { isHydrogen } from '../util/chem_utils';
import { getPreferences } from '../redux/prefs/access';

/**
 * @typedef {import('BMapsSrc/FragList').FragInfo} FragInfo
 * @typedef {import('BMapsSrc/FragList').FragSeriesInfo} FragSeriesInfo
 * @typedef {import('BMapsModel').MapCase} MapCase
 * @typedef {import('BMapsModel').CaseData} CaseData
 * @typedef {import('BMapsSrc/server_responders').ResponseData} ResponseData
 */

/**
 * @typedef FragmentsCmds
 * @type {object}
 * @property {getFragmentMap} GetFragmentMap
 * @property {releaseFragmentMap} ReleaseFragmentMap
 * @property {toggleFragmentVisibility} ToggleFragmentVisibility
 * @property {toggleFragmentSelection} ToggleFragmentSelection
 * @property {hideFragments} HideFragments
 * @property {prepareProjectCase} PrepareProjectCase
 * @property {runProjectCase} RunProjectCase
 * @property {refreshAllFragments} RefreshAllFragments
 * @property {refreshAvailableFragments} RefreshAvailableFragments
 * @property {refreshFragmentInfo} RefreshFragmentInfo
 * @property {loadFragments} LoadFragments
 * @property {loadFragmentSeries} LoadFragmentSeries
 * @property {toggleActiveFragment} ToggleActiveFragment
 * @property {toggleClusteringFragment} ToggleClusteringFragment
 * @property {makeClusterMap} MakeClusterMap
 */

/** @type {FragmentsCmds} */
export const FragmentsCmds = {
    GetFragmentMap: new UserCmd('GetFragmentMap', getFragmentMap),
    ReleaseFragmentMap: new UserCmd('ReleaseFragmentMap', releaseFragmentMap),
    // Not undoable: showing or hiding a fragment also fetches or releases its map, and the undo
    // seam restores state synchronously. They join the undoable commands with the async cut.
    ToggleFragmentVisibility: new UserCmd('ToggleFragmentVisibility', toggleFragmentVisibility),
    ToggleFragmentSelection: new UserCmd('ToggleFragmentSelection', toggleFragmentSelection),
    HideFragments: new UserCmd('HideFragments', hideFragments),
    PrepareProjectCase: new UserCmd('PrepareProjectCase', prepareProjectCase),
    RunProjectCase: new UserCmd('RunProjectCase', runProjectCase),

    RefreshAllFragments: new UserCmd('RefreshAllFragments', refreshAllFragments),
    RefreshAvailableFragments: new UserCmd('RefreshAvailableFragments', refreshAvailableFragments),
    RefreshFragmentInfo: new UserCmd('RefreshFragmentInfo', refreshFragmentInfo),
    LoadFragments: new UserCmd('LoadFragments', loadFragments),
    LoadFragmentSeries: new UserCmd('LoadFragmentSeries', loadFragmentSeries),
    ToggleActiveFragment: new UserCmd('ToggleActiveFragment', toggleActiveFragment),
    ToggleClusteringFragment: new UserCmd('ToggleClusteringFragment', toggleClusteringFragment),
    MakeClusterMap: new UserCmd('MakeClusterMap', makeClusterMap),
    DeriveWatermap: new UserCmd('DeriveWatermap', deriveWatermap),
    DeriveWatermaps: new UserCmd('DeriveWatermaps', deriveWatermaps),
};

/**
 * @description Load the fragment map for the given fragment info if not already loaded.
 * Requests the fragment summary from the server, integrates the resulting fragments
 * into the case data, and triggers a redisplay.
 * @param {FragInfo} fragmentInfo The fragment whose map should be loaded
 * @returns {Promise<void>}
 */
async function getFragmentMap(fragmentInfo) {
    const loaded = fragmentInfo.fragmentObjects;

    if (loaded.length === 0) {
        const { connector, caseData } = App.getDataParents(fragmentInfo);
        const requestObj = {
            name: fragmentInfo.name,
            series_list: fragmentInfo.series_list.map((s) => s.getSummaryRequest()),
        };
        const responseData = await doWithActiveSolute(
            fragmentInfo, () => connector.cmdGetFragmentMap(requestObj)
        );

        if (!responseData.hasError()) {
            const createdAtomGroups = integrateFragments(responseData, caseData, true);
            const fragmentObjs = createdAtomGroups.fragmentmap;
            fragmentInfo.updateFrags(fragmentObjs);
            console.log(`Loaded ${fragmentObjs.length} fragments for ${fragmentInfo.name}`);
            EventBroker.publish('redisplayFragments');
        } else {
            console.warn(`Failed to load fragments for ${fragmentInfo.name}`);
        }
    }
}

/**
 * @description Remove the loaded fragments for the given fragment info from the
 * workspace and clear the fragment objects held by the FragInfo.
 * @param {FragInfo} fragmentInfo The fragment whose fragment map should be released
 * @returns {void}
 */
function releaseFragmentMap(fragmentInfo) {
    releaseFragmentMaps([fragmentInfo]);
}

/**
 * @description Release the fragment maps of several fragments in one Workspace.removeFragments
 * call, which groups the bookkeeping by case and clears the 3D objects in one pass.
 * @param {FragInfo[]} fragmentInfos
 * @returns {void}
 */
function releaseFragmentMaps(fragmentInfos) {
    App.Workspace.removeFragments(fragmentInfos.flatMap((info) => info.fragmentObjects));
    // Clear the fragment objects in each fragmentInfo
    for (const { fragmentObjects } of fragmentInfos) {
        fragmentObjects.splice(0, fragmentObjects.length);
    }
}

/**
 * @description Fetch a fragment's map if the fragments tree now shows it (visible, or selected
 * in any of its tree nodes), or release the map if nothing shows it any more.
 * @param {FragInfo} fragmentInfo
 * @returns {Promise<void>}
 */
async function syncFragmentMap(fragmentInfo) {
    if (App.Workspace.getVisibleFragmentInfo().includes(fragmentInfo)) {
        await getFragmentMap(fragmentInfo);
    } else {
        releaseFragmentMap(fragmentInfo);
    }
}

/**
 * @description Show or hide a fragment's map summary in 3D (the fragments tree's eye icon),
 * fetching the map when the fragment becomes shown and releasing it when nothing shows it.
 * Showing is refused, silently as before, once the tree shows its limit of fragments.
 * @param {FragInfo} fragmentInfo The fragment to show or hide
 * @param {{ forceValue?: boolean }} [options] forceValue: show (true) or hide (false) instead
 *   of toggling
 * @returns {Promise<void>}
 */
async function toggleFragmentVisibility(fragmentInfo, { forceValue }={}) {
    const show = forceValue ?? !App.Workspace.isVisible(fragmentInfo);
    if (show && !App.Workspace.fragmentsTree.okToMakeVisible(fragmentInfo)) return;

    App.Workspace.toggleVisibility(fragmentInfo, show);
    await syncFragmentMap(fragmentInfo);
}

/**
 * @description Select or deselect a fragment in the fragments tree, which shows its map summary
 * in 3D with its energy filter, fetching or releasing the map as for visibility. Selection is
 * kept per tree node, not per fragment (see FragmentTree.getVisibleFragments).
 * @param {TreeNode} node The fragments tree node to select or deselect
 * @param {{ forceValue?: boolean }} [options] forceValue: select (true) or deselect (false)
 *   instead of toggling
 * @returns {Promise<void>}
 */
async function toggleFragmentSelection(node, { forceValue }={}) {
    const select = forceValue ?? !node.getNodeState().isSelected;
    if (select && !App.Workspace.fragmentsTree.okToMakeVisible(node.item)) return;

    // atomGroupStateChanged rather than the default nodeSelectionChanged: it refreshes 3D too.
    App.Workspace.toggleSelection(node, select, 'atomGroupStateChanged');
    await syncFragmentMap(node.item);
}

/**
 * @description Hide fragments and deselect fragments tree nodes, as the eye icon and deselecting
 * would one at a time: the map of every fragment left unshown is released, in one batch, and the
 * display refreshes once at the end.
 * @param {{ fragments?: FragInfo[], nodes?: TreeNode[] }} targets
 * @returns {void}
 */
function hideFragments({ fragments=[], nodes=[] }={}) {
    App.Workspace.toggleVisibility(fragments, false, null);
    App.Workspace.toggleSelection(nodes, false, null);
    const shown = App.Workspace.getVisibleFragmentInfo();
    const affected = new Set([...fragments, ...nodes.map((node) => node.item)]);
    releaseFragmentMaps([...affected].filter((fragmentInfo) => !shown.includes(fragmentInfo)));
    // Visibility handling will refresh everything in UI, including selection.
    EventBroker.publish('visibilityChanged');
}

/**
 * @description Indicate if we are ready to run the provided simulation case,
 * creating the project case files if necessary.  This calls validate-project-case
 * and write-project-case server APIs.
 * @param {{
 *  simSpec: SimSpec,
 *  useSelection: boolean,
 *  dontDeselect: boolean
 * }} param0
 * @returns {Object}
 * {
 *   status: Are we Ready or not? (from SimSpec.Status)
 *   case_suggestion: A suggestion in case of a conflict with the user's preferred case name,
 *   errors: Any errors that might have occurred,
 *   errorInfo: Structured detail for specific error statuses (e.g. bindingSiteSpecErr,
 *     failedResidues)
 * }
 */
async function prepareProjectCase({ simSpec, useSelection, dontDeselect }={}) {
    const ret = {
        status: null,
        case_suggestion: '',
        errors: [],
        errorInfo: {},
    };

    const { caseData, connector, permissionManager } = App.getDataParents(simSpec.protein);

    // Name-collision guard, ahead of any selection changes or server round trip.
    // bfd-server's validate-project-case enumerates only the USER's structures directory, so a
    // case name matching a built-in or curated structure passes its check: the client then
    // writes a prepared case into the user's store, while fragserv -- seeing an unchanged
    // sourceDesc -- classifies the run as `cp` and launches the built-in files instead, so the
    // prepare is silently discarded. Check every mounted map list here and raise the same
    // Conflict the server would, so the user renames rather than shadowing a structure.
    // Mounted sources only: a strong guard, not a guarantee; the server check is the backstop.
    const knownMapCases = App.Workspace.collectFromCaseDataCollections((cdc) => cdc.getMapList());
    const collision = SimSpec.findCaseNameCollision(simSpec, knownMapCases);
    if (collision) {
        ret.status = SimSpec.Status.Conflict;
        ret.case_suggestion = collision.suggestion;
        // Only when the clash is with the structure being simulated from can the user reuse its
        // existing setup instead; the form uses this to decide whether to offer that.
        ret.errorInfo = { collidesWithSource: collision.isSourceStructure };
        return ret;
    }

    // If using selection, residue count check and hydrogen check are skipped
    if (!useSelection) {
        const chains = [...simSpec.proteinChains, ...simSpec.polymers];
        // Check residue count
        if (!permissionManager.isAllowed('unlimited_chains')) {
            const chainLimit = 500;
            const residueCount = chains.reduce((acc, next) => acc+next.getResidues().length, 0);
            if (residueCount > chainLimit) {
                ret.status = SimSpec.Status.TooManyChainsError;
                return ret;
            }
        }

        if (chains.length === 0) {
            ret.status = SimSpec.Status.NoProteinChainsError;
            return ret;
        }

        const hasHydrogens = simSpec.proteinChains.some(
            (chain) => chain.atoms.some((atom) => isHydrogen(atom))
        );
        if (!hasHydrogens) {
            ret.status = SimSpec.Status.NoHydrogensError;
            return ret;
        }

        // Both validate-project-case and write-project-case require atoms to be selected.
        await UserActions.Select(simSpec.selectQuery, caseData);
    }
    // Find out if we need to write new case files or if we can use existing files
    let responseData = await doWithActiveSolute(
        caseData, () => connector.cmdValidateProjectCase(simSpec.forServer)
    );
    const [statusObj] = responseData.byId(ResponseIds.ProjectCaseStatus);
    let errors = [...responseData.errors];

    switch (statusObj && statusObj.status) {
        case SimSpec.CaseStatus.NEW:
            // New case: need to write the files
            responseData = await doWithActiveSolute(
                caseData, () => connector.cmdWriteProjectCase(simSpec.forServer)
            );
            errors = [...errors, ...responseData.errors]; // Handled below
            ret.status = SimSpec.Status.Ready;
            break;
        case SimSpec.CaseStatus.EXISTS_OK:
            // Can use the existing files
            ret.status = SimSpec.Status.Ready;
            break;
        case SimSpec.CaseStatus.EXISTS_CONFLICT:
            // Conflict: user needs to specify a new case name
            ret.status = SimSpec.Status.Conflict;
            ret.case_suggestion = statusObj.suggestion;
            break;
        // no default: Errors handled below
    }
    // Only deselect atoms if not using a selection
    if (!useSelection && !dontDeselect) {
        await UserActions.Select('none', caseData);
    }
    // Errors returned by either validate-project-case or write-project-case
    // will prevent the simulation from going forward.
    if (errors.length > 0) {
        ret.errors = errors;
        ret.status = SimSpec.Status.UnknownError;
        const bindingSiteErr = 'Binding site specs could not be parsed:';
        if (errors.find((e) => e.match(/out-dated (personal )?(storage )?data (re)?source/))) {
            ret.status = SimSpec.Status.StorageUpdatedError;
        } else if (errors.find((e) => e.match(/personal (storage )?data (re)?source/))) {
            ret.status = SimSpec.Status.PersonalStorageError;
        } else if (errors.find((e) => e.match(bindingSiteErr))) {
            let msg = 'Unspecified error';
            const err = errors.find((e) => e.match(bindingSiteErr));
            let lookupText = 'ParseBindingSites: ';
            let index = err.indexOf(lookupText);
            if (index > -1) {
                msg = err.substring(index + lookupText.length);
            } else {
                // Just show the whole error after the spec
                lookupText = `${bindingSiteErr}\n${simSpec.bindingSiteSpecs}`;
                index = err.indexOf(lookupText);
                msg = err.substring(index + lookupText.length);
            }
            ret.errorInfo = { bindingSiteSpecErr: msg };
            ret.status = SimSpec.Status.BindingSiteSpecError;
        } else {
            // Find residues where fdb file could not be written
            const resFdbFail = errors.map((e) => {
                const regex = /failed to write fdb file for (.+): TCL error/m;
                const regexResult = regex.exec(e);
                return regexResult && regexResult[1]; // map to null or the capture group
            }).filter((x) => x != null);
            // Find residues which have no partial charges
            const resNoCharges = errors.map((e) => {
                const regex = /Atom group (.+) does not have partial charges/m;
                const regexResult = regex.exec(e);
                return regexResult && regexResult[1]; // map to null or the capture group
            }).filter((x) => x != null);
            // 'failed to write fdb file' trumps 'does not have partial charges'
            if (resFdbFail.length > 0) {
                ret.errorInfo = { failedResidues: resFdbFail };
                ret.status = SimSpec.Status.FdbFileError;
            } else if (resNoCharges.length > 0) {
                ret.errorInfo = { failedResidues: resNoCharges };
                ret.status = SimSpec.Status.PartialChargesError;
            }
        }
    }

    return ret;
}

/**
 * @description Run the provided simulation case. This assumes PrepareProjectCase
 * has already been called. This calls run-project-case server API.
 * @param {SimSpec} simSpec - A description of the simulation case
 * @param {string} notifyUrl - URL the server should call to report job progress/completion
 * @returns {Object}
 * {
 *   errors: Any errors that might have occurred
 * }
 */
async function runProjectCase(simSpec, notifyUrl) {
    const { connector } = App.getDataParents(simSpec.protein);
    const serverArgs = { ...simSpec.forServer, 'notification-url': notifyUrl };
    const responseData = await doWithActiveSolute(
        simSpec.protein, () => connector.cmdRunProjectCase(serverArgs)
    );
    const errors = responseData.errors;
    return { errors };
}

/**
 * @description Ask every data connection's server to refresh its fragment data.
 * @returns {void}
 */
function refreshAllFragments() {
    for (const dataConnection of App.DataConnections) {
        dataConnection.getConnector().cmdRefreshFragments();
    }
}

/**
 * @description Refresh the list of available fragments for a case. Visible fragment
 * maps are hidden during the refresh and redisplayed afterwards.
 * @param {MapCase} mapCase The map case to refresh available fragments for
 * @returns {Promise<void>}
 */
async function refreshAvailableFragments(mapCase) {
    // First hide all visible fragments, redisplaying after the data refresh
    const visibleFragmentInfo = App.Workspace.getVisibleFragmentInfo();
    for (const fragInfo of visibleFragmentInfo) {
        releaseFragmentMap(fragInfo);
    }

    const { connector } = App.getDataParents(mapCase);
    const responseData = await doWithActiveSolute(
        mapCase, () => connector.cmdGetAvailableFragments(mapCase)
    );
    const [listObj] = responseData.byId(ResponseIds.AvailableFragments);
    App.Workspace.setAvailableFragments(mapCase, listObj);
    EventBroker.publish('dataLoaded');
    // Actually processing the data is handled by project_data doReceiveAvailableFragments();
    if (responseData.hasError()) {
        console.warn(`Errors refreshing available fragments: ${responseData.errors.join('; ')}`);
    }

    for (const fragInfo of visibleFragmentInfo) {
        await getFragmentMap(fragInfo);
    }

    EventBroker.publish('redisplayFragments');
}

/**
 * @description Refresh fragserv info once, then the available fragments for each case in turn,
 * enable default clustering fragments for each when no hotspots exist yet, and optionally load
 * each one's default set of fragments.
 *
 * Cases are processed strictly one at a time, each repointing the active solute immediately
 * before its own server calls: get-available-fragments and load-fragments carry no case identity
 * of their own, so a request can only land on the right case if no other case's refresh is
 * concurrently in flight to steal the ambient context back (BP2-2623). refreshFragservInfo is
 * case-independent (general fragserv metadata, not per-case data), so it only needs to run once
 * for the whole list rather than once per case.
 *
 * A failure on one case is caught and logged rather than aborting the rest of the list, and
 * still resolves that case's FragList promise (via setAvailableFragments(mapCase, [])) so the UI
 * doesn't get stuck on "Determining available fragments...".
 * @param {MapCase|MapCase[]} mapCasesIn The case(s) to refresh
 * @param {string} [fragmentLoadingOverride] Override for the FragmentLoading preference (e.g.
 *   session restore's 'lazy' override, cmds/SaveRestoreCmds.js). Omit to just use the
 *   FragmentLoading preference.
 * @returns {Promise<void>}
 */
async function refreshFragmentInfo(mapCasesIn, fragmentLoadingOverride) {
    const mapCases = [].concat(mapCasesIn); // ensure array
    const fragmentData = App.Workspace.fragmentData;
    // Case-independent fragserv metadata, so it runs once for the batch rather than per case.
    try {
        await App.Workspace.refreshFragservInfo();
    } catch (err) {
        console.warn(`Failed to refresh fragserv info; continuing with cached info: ${err}`);
    }
    const { FragmentLoading } = getPreferences();
    const fragmentLoading = fragmentLoadingOverride ?? FragmentLoading;
    // Fragments are refreshed strictly one case at a time, never in parallel,
    // since get-available-fragments and load-fragments carry no case identity.
    /* eslint-disable no-await-in-loop -- serialization is the fix, not an oversight */
    for (const mapCase of mapCases) {
        try {
            const { connector } = App.getDataParents(mapCase);
            const shouldLoadFrags = fragmentLoading === 'greedy' && !connector?.staticMode;
            await refreshAvailableFragments(mapCase);
            const availableFragInfo = App.Workspace.getAvailableFragmentInfo(mapCase);
            const defaultClusteringFrags = fragmentData
                .getAvailableClusteringFragments(availableFragInfo);
            if (
                App.Workspace.getHotspots(mapCase).length === 0
                && defaultClusteringFrags.length > 0
            ) {
                await UserActions.ToggleClusteringFragment(defaultClusteringFrags, true);
            }

            if (shouldLoadFrags) {
                await loadDefaultFragments(mapCase);
            }
        } catch (err) {
            console.warn(`Failed to refresh fragments for ${mapCase.displayName}: ${err}`);
            App.Workspace.setAvailableFragments(mapCase, []);
        }
    }
    /* eslint-enable no-await-in-loop */
}

/**
 * @description Load the default set of fragments for a case (up to 200), prioritizing
 * user fragments and then standard fragments, marking them active.
 * @param {MapCase} mapCase The map case to load default fragments for
 * @returns {Promise<ResponseData|null>} The server response, or null if no map case is found
 */
async function loadDefaultFragments(mapCase) {
    const nFragsToLoad = 200;

    const fragList = App.Workspace.getAvailableFragmentInfo(mapCase).items();
    const fragservData = App.Workspace.fragmentData.fragservData;

    const standardFragsAvailable = fragservData.getStandardFragset();
    // User fragments take priority, then lower b-values
    fragList.sort((a, b) => {
        if (a.hasUserSeries !== b.hasUserSeries
                && (a.hasUserSeries || b.hasUserSeries)) {
            return a.hasUserSeries ? -1 : 1;
        } else {
            if (!standardFragsAvailable) return 0;

            const isStandard = (f) => fragservData.isStandardFrag(f.name);
            const aIsStandard = isStandard(a);
            const bIsStandard = isStandard(b);
            if (aIsStandard !== bIsStandard) {
                return aIsStandard ? -1 : 1;
            } else {
                return 0;
            }
        }
    });

    const activeArg = fragList.map((fragItem, i) => ({
        item: fragItem,
        forceValue: i < nFragsToLoad,
    }));
    // Don't send UI updates for this active toggling (the final false does this)
    App.Workspace.toggleActive(activeArg, undefined, false);
    return loadFragments(fragList.slice(0, nFragsToLoad));
}

/**
 * @description Load all fragment series belonging to the given list of fragments.
 * @param {FragInfo[]} fragmentList The fragments whose series should be loaded
 * @returns {Promise<ResponseData|null>} The server response, or null if no map case is found
 */
async function loadFragments(fragmentList) {
    const series = fragmentList.reduce(
        (list, frag) => list.concat(frag.series_list),
        [],
    );
    return loadFragmentSeries(series);
}

/**
 * @description Request the server to load the given fragment series for their case.
 * @param {FragSeriesInfo[]} fragSeriesList The fragment series to load
 * @returns {Promise<ResponseData|null>} The server response, or null if no map case is found
 */
async function loadFragmentSeries(fragSeriesList) {
    const { connector, caseData } = App.getDataParents(fragSeriesList[0]);
    const mapCase = caseData?.mapCase;
    if (!mapCase) return null;

    const requestObj = {
        project: mapCase.project,
        case: mapCase.case,
        fragsList: fragSeriesList.map((s) => s.getLoadRequest()),
    };

    const responseData = await doWithActiveSolute(
        fragSeriesList[0], () => connector.cmdLoadFragments(requestObj)
    );
    return responseData;
}

/**
 * @description Toggle the active state of one or more fragments and reload the active
 * fragments for each affected case.
 * @param {FragInfo|FragInfo[]} fragment A fragment or array of fragments to toggle
 * @param {boolean} [forceValue] If provided, force the active state instead of toggling
 * @returns {void}
 */
async function toggleActiveFragment(fragment, forceValue) {
    App.Workspace.toggleActive(fragment, forceValue);
    const { dataParentsList } = App.partitionByDataParents([].concat(fragment)); // force to array
    // Serialized, not Promise.all: load-fragments carries no case identity of its own, so each
    // iteration depends on the active solute IT set still being current when its request lands.
    // Starting them concurrently puts every repoint on the wire before any reply arrives, and
    // all but the last case is then rejected by the server's project/case guard (BP2-2660).
    for (const { caseData } of dataParentsList) {
        await loadFragments(App.Workspace.getActiveFragments(caseData));
    }
}

/**
 * @description Toggle whether one or more fragments are used for hotspot clustering and
 * rebuild the cluster map for each affected case.
 * @param {FragInfo|FragInfo[]} fragInfo A fragment or array of fragments to toggle
 * @param {boolean} [forceValue] If provided, force the clustering state instead of toggling
 * @returns {void}
 */
async function toggleClusteringFragment(fragInfo, forceValue) {
    // A fragment with no hotspot-eligible series (e.g. only non-clustering user series) can't
    // be turned ON. Turning OFF (forceValue === false) is always allowed.
    const requested = [].concat(fragInfo); // force to array
    const toToggle = forceValue === false
        ? requested
        : requested.filter((fi) => fi.hasEligibleHotspotSeries());
    if (toToggle.length === 0) return;
    App.Workspace.toggleProp(toToggle, 'usedForHotspots', forceValue);

    const { dataParentsList } = App.partitionByDataParents(toToggle);
    // Serialized, not Promise.all -- see toggleActiveFragment above. make-clustermap has the
    // same case-less shape, and refreshFragmentInfo's `await` of this function was previously
    // awaiting `undefined`, silently disabling the per-case serialization it relies on.
    for (const { caseData } of dataParentsList) {
        await makeClusterMap(App.Workspace.getClusteringFragments(caseData), { caseData });
    }
}

/**
 * @description Build a hotspot cluster map from the given fragments by requesting a
 * clustered fragment map from the server and integrating the result into the case data.
 * @param {FragInfo|FragInfo[]} hotspotFragmentsIn A fragment or array of fragments to cluster
 * @param {{ caseData: CaseData }} param1 The case data for the request
 * @returns {Promise<ResponseData|null>} The server response, or null if no map case is found
 */
async function makeClusterMap(hotspotFragmentsIn, { caseData }) {
    const hotspotFragments = [].concat(hotspotFragmentsIn); // force array

    const mapCase = caseData.mapCase;
    if (!mapCase) return null;

    const { connector } = App.getDataParents(caseData);

    // Only hotspot-eligible series contribute to the cluster map; a non-clustering user series
    // belonging to an otherwise-eligible fragment is omitted from the request.
    const series = hotspotFragments.reduce(
        (list, frag) => list.concat(frag.series_list.filter((s) => s.hotspotEligible)),
        [],
    );
    const requestObj = {
        project: mapCase.project,
        case: mapCase.case,
        diversity: 9,
        fragsList: series.map((s) => s.getLoadRequest()),
    };

    const responseData = await doWithActiveSolute(
        caseData, () => connector.cmdMakeClusterMap(requestObj)
    );
    integrateFragments(responseData, caseData);
    return responseData;
}

/**
 * @description Ask the server to derive this case's water map and integrate the result.
 * The server reports which cases have water simulation data (watermap-availability, from
 * get-case-files); a map can be derived where that holds and none is loaded. Deriving takes
 * minutes and holds the server for the duration, so callers confirm first -- see
 * offerWatermapDerivation.
 *
 * Returns the server's errors rather than the integrated groups. integrateFragments defaults
 * returnAGs to false for water maps on purpose (see its docstring: no point building arrays of
 * thousands of waters), so its water list is empty even on success and says nothing about
 * whether the map arrived. Ask the case for that. This command is an ArrayResponse, so an
 * Error packet resolves the responder instead of rejecting it, and the server's reason for a
 * failure is only here.
 * @param {{ caseData: CaseData }} param0 The case to derive a water map for
 * @returns {Promise<{errors: Array}>} Server-reported errors, empty when the map arrived
 */
async function deriveWatermap({ caseData }) {
    const { connector, mapCase } = App.getDataParents(caseData);
    if (!mapCase) return { errors: [] };

    const responseData = await doWithActiveSolute(
        caseData, () => connector.cmdDeriveWatermap({ spec: mapCase.projectCase })
    );
    integrateFragments(responseData, caseData);
    // The Fragments tree picks Derivable vs Computed water rows only when rebuilt.
    if (caseData.hasComputedWaters()) App.Workspace.rebuildFragmentsTree();
    return { errors: responseData.errors };
}

/**
 * @description Derive a map for each case in turn, keeping whatever succeeds. A server-side
 * failure for one case must not abandon the rest, nor skip the check for waters that already
 * arrived for an earlier one -- a single try around the whole loop did both.
 *
 * Each derivation takes minutes and the server runs one command at a time, so the workspace
 * can change underneath this loop. Two things follow, and both are about cases NOT yet sent:
 *
 * - A rejection ends the loop. This command is an ArrayResponse: server-side failures come
 *   back as errors, so only a reset or a closed connection rejects. Every case still to come
 *   then belongs to a session that no longer exists.
 * - A removal or a zap is only confirmed by the server once the running derivation finishes,
 *   so the client learns of it far too late to help. The caseRemovalRequested and zapRequested
 *   events fire when the user ASKS, before the first await, and that is what this listens to.
 *   A "still loaded" test at send time cannot do it: the client has not been told yet.
 * @param {CaseData[]} caseDataList
 * @returns {Promise<{errors: string[], derived: CaseData[]}>} errors holds one entry per
 *      failure, server-reported or thrown, each named by its case so several cases can be told
 *      apart; derived holds the cases that were actually sent and answered, which is what the
 *      caller should report on -- skipped and unreached cases are in neither
 */
async function deriveWatermaps(caseDataList) {
    const errors = [];
    const derived = [];
    const removalRequested = new Set();
    let zapRequested = false;
    const onRemoval = (event, { caseData }={}) => removalRequested.add(caseData);
    const onZap = () => { zapRequested = true; };
    EventBroker.subscribe('caseRemovalRequested', onRemoval);
    EventBroker.subscribe('zapRequested', onZap);

    try {
        for (const caseData of caseDataList) {
            if (zapRequested) break;
            if (removalRequested.has(caseData)) continue;
            try {
                // Sequential on purpose: the server handles one command at a time anyway.
                // eslint-disable-next-line no-await-in-loop
                const { errors: caseErrors } = await UserActions.DeriveWatermap({ caseData });
                derived.push(caseData);
                errors.push(...caseErrors.map((e) => `${caseData.getName()}: ${e}`));
            } catch (err) {
                // A socket drop or a zap-all during the derivation rejects the responder.
                errors.push(`${caseData.getName()}: ${err?.message || err}`);
                break;
            }
        }
    } finally {
        EventBroker.unsubscribe('caseRemovalRequested', onRemoval);
        EventBroker.unsubscribe('zapRequested', onZap);
    }
    return { errors, derived };
}
